import { BulbOutlined } from "@ant-design/icons";
import { Handle, Node, NodeProps, Position, useConnection, useHandleConnections } from "@xyflow/react";
import { Tag, Tooltip } from "antd";
import mergeClassNames from "common/mergeClassNames";
import { useAppSelector } from "hooks";
import { memo } from "react";

export type ITargetNodeDataType = {
    field: IDataMappingField;
    aiSuggestion?: {
        sourceColumn: string;
        targetAttribute: string;
        confidence: number;
        reasoning: string;
        sourceColumnExists: boolean;
        targetAttributeExists: boolean;
    };
};

export type ITargetNodeType = Node<ITargetNodeDataType, "targetNode">;

function TargetNode(targetProps: NodeProps<ITargetNodeType>) {
    const clickConnecting = useAppSelector((state) => state.dataMapperFlow.clickConnecting);
    const connection = useConnection();
    const { id, isConnectable, data, targetPosition } = targetProps;

    const targetConnections = useHandleConnections({
        type: "target",
        id: "handle-target-input",
    });

    const mappedConnections = useHandleConnections({
        type: "source",
        id: "handle-target-output",
    });

    const isTarget = connection.inProgress && connection.fromNode.id !== id && connection.fromNode.type === "sourceNode";

    return (
        <>
            {isConnectable && targetPosition && (
                <Handle
                    id="handle-target-input"
                    type="target"
                    position={targetPosition}
                    className="target-handle-input !-left-2 !top-8 !size-4"
                    style={{
                        visibility: targetConnections.length > 0 || isTarget || clickConnecting ? "visible" : "hidden",
                    }}
                />
            )}
            <div
                className={mergeClassNames(
                    "flex min-h-16 min-w-40 flex-col items-center justify-center gap-1 rounded-lg rounded-tr-none bg-emerald-100 p-3",
                    mappedConnections.length === 0 ? "outline-dashed outline-1 outline-offset-1 outline-red-400" : "",
                )}
            >
                <span className="text-sm">{data.field.name}</span>
                {data.field.dataType && (
                    <div className="absolute -top-2 right-0 rounded-bl-lg rounded-tl-sm rounded-tr-lg bg-emerald-200 px-2 py-[0.3em] text-[0.6em]">{data.field.dataType}</div>
                )}

                {/* AI Suggestion Display */}
                {data.aiSuggestion && mappedConnections.length === 0 && (
                    <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 transform">
                        <Tooltip title={data.aiSuggestion.reasoning} placement="top">
                            <div className="flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-1">
                                <BulbOutlined className="text-xs text-blue-600" />
                                <Tag color={data.aiSuggestion.confidence > 0.8 ? "green" : data.aiSuggestion.confidence > 0.6 ? "orange" : "red"} className="text-xs">
                                    {Math.round(data.aiSuggestion.confidence)}%
                                </Tag>
                                <span className="text-xs text-blue-800">{data.aiSuggestion.sourceColumn}</span>
                            </div>
                        </Tooltip>
                    </div>
                )}
            </div>
            {isConnectable && (
                <Handle
                    id="handle-target-output"
                    type="source"
                    position={Position.Right}
                    isConnectable={targetConnections.length === 0}
                    className={[
                        "target-handle-output !-right-2 !top-8 flex !size-4 items-center justify-center",
                        targetConnections.length > 0 ? "!bg-emerald-600" : "!bg-red-800",
                    ].join(" ")}
                />
            )}
        </>
    );
}

export default memo(TargetNode);
